import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  Tab,
  Tabs,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Shield as ShieldIcon,
  Business as BusinessIcon,
  VpnKey as ApiKeyIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api, { authAPI, companiesAPI } from '../services/api';
import { useLocation } from 'react-router-dom';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import useResponsive from '../hooks/useResponsive';
import { logger } from '../utils/logger';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// API Keys Tab Content Component
const APIKeysTabContent: React.FC = () => {
  const [keyInfo, setKeyInfo] = useState<{created_at: string, tier: string, rate_limit_per_day: number} | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);

  useEffect(() => {
    fetchKeyInfo();
  }, []);

  const fetchKeyInfo = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/api-keys/info').catch((err: any) => {
        if (err.response?.status === 404) return null;
        throw err;
      });
      if (!response) {
        setKeyInfo(null);
      } else {
        setKeyInfo(response.data);
      }
    } catch (err: any) {
      logger.error('Failed to fetch API key info:', err);
      setError(err.message || 'Greška pri učitavanju informacija o API ključu');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    setGenerating(true);
    setError(null);

    try {
      const response = await api.post('/api-keys/generate');
      const data = response.data;
      setGeneratedKey(data.api_key);
      setSuccess(data.message);
      // Refresh info
      fetchKeyInfo();
    } catch (err: any) {
      logger.error('Failed to generate API key:', err);
      setError(err.message || 'Greška pri generisanju API ključa');
    } finally {
      setGenerating(false);
    }
  };

  const handleRevokeKey = async () => {
    setRevoking(true);
    setError(null);

    try {
      await api.delete('/api-keys/revoke');

      setSuccess('API ključ je uspešno povučen.');
      setRevokeDialogOpen(false);
      setKeyInfo(null);
      setGeneratedKey(null);
    } catch (err: any) {
      logger.error('Failed to revoke API key:', err);
      setError(err.message || 'Greška pri povlačenju API ključa');
    } finally {
      setRevoking(false);
    }
  };

  const handleCopyKey = () => {
    if (generatedKey) {
      navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case 'free': return 'default';
      case 'basic': return 'primary';
      case 'premium': return 'secondary';
      case 'enterprise': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sr-RS', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <ApiKeyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            API Ključevi
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            API ključ omogućava vašoj kompaniji da koristi OmniPass API za kreiranje i upravljanje pass-ovima programski.
          </Typography>

          {!keyInfo && !generatedKey ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Trenutno nemate generisan API ključ. Kliknite na dugme ispod da generišete novi ključ.
              </Alert>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleGenerateKey}
                disabled={generating}
              >
                {generating ? 'Generisanje...' : 'Generiši API Ključ'}
              </Button>
            </Box>
          ) : generatedKey ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>Važno:</strong> Ovo je jedina prilika da vidite vaš API ključ. Kopirajte ga na sigurno mesto.
                Nakon što zatvorite ovu stranicu, nećete moći ponovo da ga vidite.
              </Alert>
              <Box sx={{
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2
              }}>
                <Typography sx={{ wordBreak: 'break-all' }}>
                  {generatedKey}
                </Typography>
                <IconButton onClick={handleCopyKey} color="primary">
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </IconButton>
              </Box>
            </Box>
          ) : keyInfo ? (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Tier
                  </Typography>
                  <Chip
                    label={keyInfo.tier.toUpperCase()}
                    color={getTierColor(keyInfo.tier) as any}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Rate Limit
                  </Typography>
                  <Typography variant="body1">
                    {keyInfo.rate_limit_per_day.toLocaleString()} poziva/dan
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary">
                    Kreiran
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(keyInfo.created_at)}
                  </Typography>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mb: 2 }}>
                API ključ je sakriven iz bezbednosnih razloga. Čuvate ga na sigurnom mestu.
              </Alert>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleGenerateKey}
                  disabled={generating}
                >
                  {generating ? 'Generisanje...' : 'Regeneriši Ključ'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setRevokeDialogOpen(true)}
                >
                  Povuci Ključ
                </Button>
              </Box>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* Revoke Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => !revoking && setRevokeDialogOpen(false)}>
        <DialogTitle>Povuci API Ključ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Da li ste sigurni da želite da povučete vaš API ključ?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Upozorenje:</strong> Ova akcija ne može biti poništena. Sve aplikacije koje koriste ovaj ključ
            će prestati da rade. Moraćete da generišete novi ključ ako želite da nastavite sa korišćenjem API-ja.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)} disabled={revoking}>
            Otkaži
          </Button>
          <Button
            onClick={handleRevokeKey}
            color="error"
            variant="contained"
            disabled={revoking}
          >
            {revoking ? 'Povlačim...' : 'Povuci Ključ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper function to get initial tab from URL
const getInitialTab = (search: string) => {
  const params = new URLSearchParams(search);
  const tabParam = params.get('tab');
  switch(tabParam) {
    case 'company': return 1;      // Kompanija
    default: return 0;             // Profil
  }
};

const AccountSettings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { isMobile } = useResponsive();
  const location = useLocation();

  const [tabValue, setTabValue] = useState(() => getInitialTab(location.search));
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordCooldown, setPasswordCooldown] = useState(0);

  // Company data (only fields that have values in backend)
  const [companyData, setCompanyData] = useState({
    company_name: '',
    display_name: '',
    industry: '',
    company_size: '',
    primary_email: '',
    tier: '',
    status: ''
  });

  // Profile form data (modified to remove unwanted fields)
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Password form data
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Update tab when URL changes (only on mount or actual URL change)
  useEffect(() => {
    const newTab = getInitialTab(location.search);
    setTabValue(newTab);
  }, [location.search]); // Removed tabValue dependency

  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        // Load user profile data
        setProfileData({
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          email: user.email || '',
          phone: user.phone || ''
        });

        // Load company data from API
        try {
          const company = await companiesAPI.getMyCompany();
          setCompanyData({
            company_name: company.company_name || '',
            display_name: company.display_name || '',
            industry: company.industry || '',
            company_size: company.company_size || '',
            primary_email: company.primary_email || '',
            tier: company.tier || '',
            status: company.status || ''
          });
        } catch (error) {
          logger.error('Error loading company data:', error);
        }
      }
    };

    loadUserData();
  }, [user]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.patch('/users/me', {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        phone: profileData.phone
      });

      setSuccess('Profil je uspešno ažuriran!');
      // Refresh user data
      await refreshUser();
    } catch (err) {
      setError('Greška pri ažuriranju profila. Pokušajte ponovo.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordCooldown > 0) return;

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Nova lozinka i potvrda se ne poklapaju!');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('Nova lozinka mora imati najmanje 8 karaktera!');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await authAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      setSuccess('Lozinka je uspešno promenjena!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordDialogOpen(false);
      // 60-second cooldown to prevent rapid repeated submissions
      setPasswordCooldown(60);
      const timer = setInterval(() => {
        setPasswordCooldown(prev => {
          if (prev <= 1) { clearInterval(timer); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      logger.error('Password change error:', err);
      const errorMessage = err.response?.data?.detail || 'Greška pri promeni lozinke. Pokušajte ponovo.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // API Key Management Functions
  return (
    <Box sx={{
      p: 3,
      m: 0,
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #f7faff 0%, #dfeafc 50%, #abc3ea 100%)',
      position: 'relative',
    }}>
      {/* Header Section */}
      <ResponsivePageHeader
        title="Moj Nalog"
        subtitle="Upravljajte profilom, bezbednošću i podešavanjima naloga"
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
      />

      {/* Success/Error Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Card>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab icon={<PersonIcon />} label="Profil" />
            <Tab icon={<BusinessIcon />} label="Kompanija" />
            <Tab icon={<ApiKeyIcon />} label="API Ključevi" />
          </Tabs>
        </Box>

        {/* Profile Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            {/* Profile Form */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Informacije o profilu
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Ime"
                        value={profileData.firstName}
                        onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Prezime"
                        value={profileData.lastName}
                        onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                        required
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={profileData.email}
                        disabled
                        helperText="Email adresa se ne može menjati"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Telefon"
                        value={profileData.phone}
                        onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </Grid>
                  </Grid>

                  <Box mt={3}>
                    <Button
                      variant="contained"
                      onClick={handleProfileUpdate}
                      loading={loading}
                      disabled={loading}
                    >
                      Sačuvaj promene
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Password Change Section */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <ShieldIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Lozinka
                  </Typography>

                  {user?.azure_ad_user_id && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Vaš nalog je sinhronizovan sa Microsoft Entra ID sistemom.
                        Promena lozinke će se automatski primeniti na oba sistema.
                      </Typography>
                    </Alert>
                  )}

                  <Typography variant="body2" color="text.secondary" paragraph>
                    Redovno menjanje lozinke povećava bezbednost naloga.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setPasswordDialogOpen(true)}
                    fullWidth={isMobile}
                  >
                    Promenite lozinku
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Company Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            {/* Basic Company Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Osnovne informacije
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Naziv kompanije"
                        value={companyData.company_name}
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Prikazano ime"
                        value={companyData.display_name}
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Industrija"
                        value={companyData.industry}
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Veličina kompanije"
                        value={companyData.company_size}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Contact Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Kontakt informacije
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label="Primarni email"
                        value={companyData.primary_email}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Plan & Status */}
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Plan i status
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Trenutni plan"
                        value={companyData.tier}
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Status"
                        value={companyData.status}
                        disabled
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Info Message */}
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Napomena:</strong> Informacije o kompaniji su samo za čitanje.
                  Za izmene kontaktirajte administratora ili korisničku podršku.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        {/* API Keys Tab */}
        <TabPanel value={tabValue} index={2}>
          <APIKeysTabContent />
        </TabPanel>

        {/* Notifications Tab */}

      </Card>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Promena lozinke</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Trenutna lozinka"
              type={showCurrentPassword ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
            <TextField
              fullWidth
              label="Nova lozinka"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
              margin="normal"
              helperText="Lozinka mora imati najmanje 8 karaktera"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
            <TextField
              fullWidth
              label="Potvrdite novu lozinku"
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              margin="normal"
              error={passwordData.confirmPassword !== '' && passwordData.newPassword !== passwordData.confirmPassword}
              helperText={
                passwordData.confirmPassword !== '' && passwordData.newPassword !== passwordData.confirmPassword
                  ? 'Lozinke se ne poklapaju'
                  : ''
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>
            Otkaži
          </Button>
          <Button
            onClick={handlePasswordChange}
            disabled={!passwordData.currentPassword || !passwordData.newPassword || passwordData.newPassword !== passwordData.confirmPassword || passwordCooldown > 0}
            variant="contained"
          >
            {passwordCooldown > 0 ? `Sačekajte ${passwordCooldown}s` : 'Promenite lozinku'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountSettings;
